import type Quill from 'quill/core/quill'
import merge from 'lodash/merge'
import Delta from 'quill-delta'
import { DeleteAction, ImageSpec, ResizeAction } from 'quill-blot-formatter'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useCallback, useEffect, useState } from 'react'

import { type QuillEditorProps } from '..'
import { toast } from '@/components/toast'
import useFileArchiveUpload from '@/utils/hooks/useFileArchiveUpload'
import { useCompany } from '@/utils/hooks/useCompany'
import {
  endImageBlock,
  ImageAlignAction,
  startImageBlock,
} from './ImageAlignment'
import { docxToHtml, type UploadedImage } from './import-docx'
import { pdfToHtml } from './import-pdf'
import { pastedImageFiles } from './pasted-image-files'

class CustomImageSpec extends ImageSpec {
  getActions() {
    return [ImageAlignAction, DeleteAction, ResizeAction]
  }
}

type Props = {
  isInitialized: boolean
  quillEditorRef: React.RefObject<Quill | null>
  setTablePickerVisible: React.Dispatch<React.SetStateAction<boolean>>
  isMobile: boolean
  placeholder: QuillEditorProps['placeholder']
}

type Return = NonNullable<QuillEditorProps['options']> | null

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const DOCX_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
const IMPORT_TYPES = ['application/pdf', '.pdf', DOCX_TYPE, '.docx']

const isPdf = (file: File) =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
const isDocx = (file: File) =>
  file.type === DOCX_TYPE || /\.docx$/i.test(file.name)

const useOptions = (props: Props): Return => {
  const {
    isInitialized,
    quillEditorRef,
    setTablePickerVisible,
    isMobile,
    placeholder,
  } = props

  const translations = useTranslate({
    alignLeft: 'quill.align-image-left',
    alignRight: 'quill.align-image-right',
    alignBreakText: 'quill.align-image-break-text',
    importDocument: 'quill.import-document',
    importDocumentLoading: 'quill.import-document-loading',
    importDocumentNoText: 'quill.import-document-no-text',
    importDocumentFailed: 'quill.import-document-failed',
  })

  const { id: companyId } = useCompany()
  const [uploadImageMutation] = useFileArchiveUpload({
    onCompleted: () => {
      // Handle successful upload if needed
    },
  })

  const [options, setOptions] = useState<NonNullable<
    QuillEditorProps['options']
  > | null>(null)

  const uploadImage = useCallback(
    async (file: File): Promise<UploadedImage | null> => {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) return null

      try {
        const response = await uploadImageMutation({
          variables: {
            input: {
              file: file,
              description: '',
              company: companyId,
              status: 'PENDING',
            },
          },
        })

        const { id, url } = response.data?.createFileArchive.fileArchive ?? {}
        if (id && url) return { id, url }
        throw new Error('No image URL returned from upload')
      } catch (error) {
        console.error('Upload failed', error)
        return null
      }
    },
    [companyId, uploadImageMutation]
  )

  const uploadImageAndInsertAtSelection = useCallback(
    async (file: File) => {
      if (!quillEditorRef.current) return

      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        alert('Unsupported image type!')
        return
      }

      const quill = quillEditorRef.current
      const range = quill.getSelection(true) ?? {
        index: 0,
        length: 0,
      }

      const image = await uploadImage(file)
      if (!image) return

      const index = startImageBlock(quill, range.index)
      quill.insertEmbed(index, 'image', image.url)
      quill.formatText(index, 1, {
        width: '100%',
        height: 'auto',
        'data-id': image.id,
      })
      quill.setSelection(endImageBlock(quill, index))
    },
    [quillEditorRef, uploadImage]
  )

  const importDocumentAtSelection = useCallback(
    async (file: File) => {
      const quill = quillEditorRef.current
      if (!quill) return

      if (!isPdf(file) && !isDocx(file)) {
        toast.error(translations.importDocumentFailed)
        return
      }

      const range = quill.getSelection(true) ?? { index: 0, length: 0 }
      const toastId = toast.loading(translations.importDocumentLoading)

      try {
        const html = isPdf(file)
          ? await pdfToHtml(file)
          : await docxToHtml(file, uploadImage)
        if (!html) {
          toast.error(translations.importDocumentNoText, { id: toastId })
          return
        }

        const content = quill.clipboard.convert({ html })
        quill.updateContents(
          new Delta().retain(range.index).delete(range.length).concat(content),
          'user'
        )
        quill.setSelection(range.index + content.length(), 'silent')
        toast.dismiss(toastId)
      } catch (error) {
        console.error('Document import failed', error)
        toast.error(translations.importDocumentFailed, { id: toastId })
      }
    },
    [
      quillEditorRef,
      translations.importDocumentFailed,
      translations.importDocumentLoading,
      translations.importDocumentNoText,
      uploadImage,
    ]
  )

  useEffect(() => {
    if (isInitialized) return // Make sure to only set options once

    const defaultOptions: NonNullable<QuillEditorProps['options']> = {
      theme: 'snow',
      placeholder,
      modules: {
        table: true,
        blotFormatter: {
          specs: [CustomImageSpec],
          align: {
            titles: {
              left: translations.alignLeft,
              right: translations.alignRight,
              breakText: translations.alignBreakText,
            },
            toolbar: {
              allowDeselect: false,
              buttonStyle: {
                boxSizing: 'border-box',
                cursor: 'pointer',
              },
              svgStyle: {
                display: 'block',
                width: '18px',
                height: '18px',
                margin: '2px',
                background: 'none',
                border: 'none',
              },
            },
          },
        },
        toolbar: {
          container: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [
              { align: '' },
              { align: 'center' },
              { align: 'right' },
              { align: 'justify' },
            ],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            ['blockquote', 'link'],

            [{ list: 'ordered' }, { list: 'bullet' }],
            ['image', 'video', 'import'],
            ['table'],
          ],
          handlers: {
            table: function (value: any) {
              if (value) {
                setTablePickerVisible(true)
              }
            },
            image: () => {
              // 1. Lag din egen input med 'accept'
              const input = document.createElement('input')
              input.setAttribute('type', 'file')
              input.setAttribute('accept', ALLOWED_IMAGE_TYPES.join(','))
              input.click()

              input.onchange = async () => {
                if (!quillEditorRef.current || !input?.files) return

                const file = input.files?.[0]
                if (!file) return
                await uploadImageAndInsertAtSelection(file)
              }
            },
            import: () => {
              const input = document.createElement('input')
              input.setAttribute('type', 'file')
              input.setAttribute('accept', IMPORT_TYPES.join(','))
              input.click()

              input.onchange = async () => {
                const file = input.files?.[0]
                if (file) await importDocumentAtSelection(file)
              }
            },
          },
        },
      },
    }

    const mobileOptions: NonNullable<QuillEditorProps['options']> = {
      modules: {
        toolbar: {
          container: [
            { header: [1, 2, 3, 4, 5, 6, false] },
            { align: '' },
            { align: 'center' },
            { align: 'right' },
            { align: 'justify' },
            'bold',
            'italic',
            'underline',
            'strike',
            { color: [] },
            { background: [] },
            'blockquote',
            'link',
            { list: 'ordered' },
            { list: 'bullet' },
            'image',
            'video',
            'import',
            'table',
          ],
        },
      },
    }

    if (isMobile) setOptions(merge({}, defaultOptions, mobileOptions))
    else setOptions(defaultOptions)
  }, [
    importDocumentAtSelection,
    isMobile,
    isInitialized,
    placeholder,
    quillEditorRef,
    setTablePickerVisible,
    translations.alignBreakText,
    translations.alignLeft,
    translations.alignRight,
    uploadImageAndInsertAtSelection,
  ])

  useEffect(() => {
    const quill = quillEditorRef.current
    if (!isInitialized || !quill) return

    const toolbar = quill.getModule('toolbar') as { container?: HTMLElement }
    toolbar.container
      ?.querySelector('.ql-import')
      ?.setAttribute('title', translations.importDocument)
  }, [isInitialized, quillEditorRef, translations.importDocument])

  useEffect(() => {
    const quill = quillEditorRef.current
    if (!isInitialized || !quill) return

    const handlePaste = (event: ClipboardEvent) => {
      const imageFiles = pastedImageFiles(event.clipboardData)
      if (!imageFiles.length) return

      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      for (const file of imageFiles) {
        void uploadImageAndInsertAtSelection(file)
      }
    }

    const handleDrop = (event: DragEvent) => {
      const files = Array.from(event.dataTransfer?.files ?? []).filter(file =>
        file.type.startsWith('image/')
      )

      if (!files.length) return

      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      quill.focus()

      for (const file of files) {
        void uploadImageAndInsertAtSelection(file)
      }
    }

    quill.root.addEventListener('paste', handlePaste, true)
    quill.root.addEventListener('drop', handleDrop, true)

    return () => {
      quill.root.removeEventListener('paste', handlePaste, true)
      quill.root.removeEventListener('drop', handleDrop, true)
    }
  }, [isInitialized, quillEditorRef, uploadImageAndInsertAtSelection])

  return options
}

export default useOptions
